import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

import HomeIcon from "@mui/icons-material/Home";

import { intersection } from "lodash";

import { useAppSelector } from "app/hooks";
import {
  CALL_BACK_LIST_FEATURE,
  CLEARTONE_RESTRICTIONS,
  VERTICAL_SOUTHWESTERN_LIMITATIONS,
  VOB_REQUEST_FEATURE
} from "constants/feature-flags";
import { ADMIN_LEVEL_GROUPS } from "constants/staff";
import { useSchedulerChoice } from "features/app-layout/scheduler-choice/use-scheduler-choice.hook";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import useCanUseVoiceScreens from "hooks/use-can-use-voice-screens.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetMySendBirdDetailsQuery } from "services/users";
import {
  hasAccessRestrictions,
  hasRequiredAccess
} from "utils/auth-access.utils";

import { MENU, MenuItemConfig } from "./menu";

const CLEARTONE_RESTRICTED_PATHS = ["/inventory", "/billing", "/eras"];

export const HOME_MENU_ITEM: MenuItemConfig = {
  to: "/home",
  primary: "Home",
  Icon: HomeIcon,
  isFallbackPath: false,
  tooltip: "Home"
};

/**
 * The nav items this user can actually reach, in menu order, Home first.
 *
 * Extracted from `LayoutSideMenu` so the side menu is not the only thing that
 * knows what a user is entitled to see — the quick launcher consumes the same
 * list, which is the only way it can avoid offering dead links. The side menu
 * still owns its own badge counts and spacing; this hook is visibility only.
 *
 * Safe to call from more than one place: the single RTK query it makes
 * (`getMySendBirdDetails`) is deduped and cached by RTK Query, and every other
 * input is Redux state or a LaunchDarkly flag.
 */
export default function useVisibleMenuItems(): MenuItemConfig[] {
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);

  const isCleartoneRestrictionsEnabled = flags[CLEARTONE_RESTRICTIONS];
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];

  const { data: sendbirdDetails } = useGetMySendBirdDetailsQuery(undefined, {
    skip: !currentUser?.sendbird_user_id
  });

  const {
    userRoles,
    userPermissions,
    isScheduler,
    canAccessDashboards,
    canAccessQBO,
    canAccessCampaigns,
    isReportsOnlyUser
  } = useUserRoles();

  const canUseVoiceScreens = useCanUseVoiceScreens();

  const isQuickbooksEnabled = Boolean(
    currentPractice?.is_quickbooks_enabled && canAccessQBO
  );

  // ENG-1981: one Scheduling entry, opening whichever scheduler this person chose. With
  // development access, someone still on classic also gets V3's own entry; once opted in,
  // Scheduling already is V3, so it would be a second link to the same page.
  const { useV3, devAccess } = useSchedulerChoice();

  return useMemo(() => {
    const visible = [HOME_MENU_ITEM, ...MENU].filter(
      ({
        roles,
        permissions,
        featureFlag,
        practiceFlag,
        userFlag,
        hideFlag,
        to
      }) => {
        // Financial Reports Only Access (accountant/bookkeeper restriction): Reports plus their
        // own Settings is the whole app (Settings mirrors the ReportsOnlyGuard allow-list — the
        // account menu links there), plus QuickBooks when the user holds the "Access to
        // QuickBooks" grant and the practice has QBO enabled (bookkeepers are QuickBooks'
        // persona; ReportsOnlyGuard allows the same pair). Checked before every other rule —
        // including role fallthroughs — so it applies whatever groups the user is in.
        if (isReportsOnlyUser) {
          return (
            to === "/reports" ||
            to === "/settings" ||
            (to === "/quickbooks" && isQuickbooksEnabled)
          );
        }

        if (to === "/scheduling-v3" && (!devAccess || useV3)) return false;

        if (isScheduler) {
          // Front-desk schedulers read SOPs too; still gated on its flag.
          if (to === "/sops") return Boolean(featureFlag && flags[featureFlag]);
          const allowedPaths = [
            "/home",
            "/scheduling",
            "/patients",
            "/settings",
            "/scheduling-v3"
          ];
          return allowedPaths.includes(to);
        }

        if (to === "/quickbooks") {
          return isQuickbooksEnabled;
        }
        if (to === "/dashboard") {
          return (
            canAccessDashboards &&
            (!featureFlag || flags[featureFlag]) &&
            (!hideFlag || !flags[hideFlag])
          );
        }
        if (to === "/marketing") {
          // The Call Back List makes Marketing visible to ALL staff when its flag is on
          // (they only see that one tab inside); the classic gating applies otherwise.
          if (flags[CALL_BACK_LIST_FEATURE]) {
            return true;
          }
          return (
            (canAccessCampaigns ||
              (currentUser &&
                intersection(roles || [], userRoles).length > 0)) &&
            (!featureFlag || flags[featureFlag])
          );
        }
        // /calls follows Voice access (a seat, or Suno staff on a Voice
        // practice), never a role — the same rule as the /calls/* route
        // (ENG-2315). suno-comms voice is retired (SN-19246).
        if (to === "/calls") {
          return canUseVoiceScreens;
        }
        if (to === "/chat") {
          return Boolean(
            currentPractice?.sendbird_chat_enabled &&
              currentUser?.sendbird_user_id &&
              sendbirdDetails?.access_token
          );
        }
        if (to === "/collections") {
          return Boolean(
            // Access follows the user's permission, not the practice's enrollment: collections
            // outlive an unenrolled practice and someone has to be able to close them.
            currentUser?.has_access_to_collections
          );
        }
        if (to === "/vobs") {
          return Boolean(
            flags[VOB_REQUEST_FEATURE] &&
              (currentClinic?.has_active_provider_integration ||
                currentPractice?.has_active_provider_integration)
          );
        }
        if (
          CLEARTONE_RESTRICTED_PATHS.includes(to) &&
          isCleartoneRestrictionsEnabled
        ) {
          return (
            currentUser &&
            intersection(ADMIN_LEVEL_GROUPS, userRoles).length > 0
          );
        }
        if (to === "/faxes-portal" && verticalSouthwesternLimitationsEnabled) {
          return (
            currentUser &&
            intersection(ADMIN_LEVEL_GROUPS, userRoles).length > 0
          );
        }
        return (
          (!featureFlag || flags[featureFlag]) &&
          (!practiceFlag || Boolean(currentPractice?.[practiceFlag])) &&
          (!userFlag || Boolean(currentUser?.[userFlag])) &&
          (!hideFlag || !flags[hideFlag]) &&
          (!hasAccessRestrictions(roles, permissions) ||
            hasRequiredAccess(
              currentUser,
              userRoles,
              userPermissions,
              roles,
              permissions
            ))
        );
      }
    );
    return useV3
      ? visible.map((item) =>
          item.to === "/scheduling" ? { ...item, to: "/scheduling-v3" } : item
        )
      : visible;
  }, [
    useV3,
    devAccess,
    isReportsOnlyUser,
    isScheduler,
    isQuickbooksEnabled,
    canAccessDashboards,
    canAccessCampaigns,
    isCleartoneRestrictionsEnabled,
    verticalSouthwesternLimitationsEnabled,
    canUseVoiceScreens,
    flags,
    currentUser,
    userRoles,
    userPermissions,
    currentPractice,
    sendbirdDetails?.access_token,
    currentClinic?.has_active_provider_integration
  ]);
}
